<script setup lang="ts">
import GroupIcon from '@/assets/icons/GroupIcon.vue';
import { useStudentRecipientLabel } from '@/composables/useStudentRecipientLabel';
import UserAvatarGroup from '@/core/elements/UserAvatarGroup.vue';
import { TIMELINE_TONE_CLASSES, TimelineTone, type TTimelineTone } from '@/core/timeline/timelineTone';
import type { IGenericUser } from '@/interfaces';
import { computed } from 'vue';

const MAX_AVATARS = 3;

const props = withDefaults(
  defineProps<{
    /** Student view: only this student is named, and the remaining `students` become "+n weitere". */
    wholeClassLabel?: string;
    wholeClassSelected?: boolean;
    focusedStudent?: IGenericUser | null;
    /** Label color for surfaces outside the timeline tones. */
    labelClass?: string;
    /** Every student the entry addresses, including the focused one. */
    students: IGenericUser[];
    tone?: TTimelineTone;
  }>(),
  {
    wholeClassLabel: undefined,
    focusedStudent: null,
    labelClass: undefined,
    tone: TimelineTone.Default,
  },
);

const { getStudentRecipientLabel } = useStudentRecipientLabel();
const recipientLabel = computed(() =>
  getStudentRecipientLabel({ focusedStudent: props.focusedStudent, students: props.students }),
);
const shownStudents = computed<IGenericUser[]>(() => (props.focusedStudent ? [props.focusedStudent] : props.students));
const label = computed(() => (props.wholeClassSelected ? props.wholeClassLabel : recipientLabel.value.label));
const labelColorClass = computed(() => props.labelClass ?? TIMELINE_TONE_CLASSES[props.tone].accentTextClass);
</script>

<template>
  <div class="flex min-w-0 items-center gap-sm">
    <span
      v-if="wholeClassSelected"
      aria-hidden="true"
      class="flex size-icon-md shrink-0 items-center justify-center rounded-full bg-surface-tint text-tint"
    >
      <GroupIcon class="size-icon-smd" />
    </span>
    <UserAvatarGroup v-else aria-hidden="true" :users="shownStudents" :max="MAX_AVATARS" size="md" class="shrink-0" />
    <span class="flex min-w-0 items-baseline gap-xs">
      <span class="min-w-0 truncate text-footer-semibold" :class="labelColorClass" :title="label">{{ label }}</span>
      <span
        v-if="recipientLabel.additionalLabel"
        class="shrink-0 text-footer"
        :class="labelColorClass"
        :title="recipientLabel.additionalLabel"
      >
        {{ recipientLabel.additionalLabel }}
      </span>
    </span>
  </div>
</template>
